import Big from 'big.js';
import { subMinutes } from 'date-fns';
import { compact, max, uniq } from 'lodash-es';
import type { ReactNode } from 'react';
import { formattedDateForSubscription } from '../utils';
import { includesEnumValue, includesEnumValueForSet } from '../utils/enumlikeHandlers';
import { isOrderComplete } from '../utils/isOrderComplete';
import { isOrderPaused } from '../utils/isOrderPaused';
import { isOrderPending } from '../utils/isOrderPending';
import { percentToBps, toBigWithDefault } from '../utils/number';
import { prettyName } from '../utils/string';
import { getEntitySubAccountNames, type AllocationBE } from './Allocation';
import type { CustomerOrder, PricingParameters as CustomerPricingParameters } from './CustomerOrder';
import type { CustomerDealSummary } from './CustomerOrderSummary';
import { getFixingIndicesList, type FixingDetails } from './FixingDetails';
import { HedgeOrderStatus } from './HedgeOrderStatus';
import { WarningSeverity } from './WarningSeverity';
import type { OrdStatusEnum } from './types';
import {
  ArchivedStatusEnum,
  DecisionStatusEnum,
  MultilegReportingTypeEnum,
  OrdTypeEnum,
  OrderMarketStatusEnum,
  ParentOrderTypeEnum,
  ReduceFirstEnum,
  ReduceOnlyEnum,
  RequestSourceEnum,
  UnifiedLiquidityEnum,
  type CxlRejReasonEnum,
  type ExecTypeEnum,
  type ExpressedOutcomeEnum,
  type IHedgeOrderStatus,
  type IOMSExecutionReport4203LegSummary,
  type IOMSExecutionReport4203Markets,
  type IStrategyParameters,
  type OrdRejReasonEnum,
  type OrdRiskStatusEnum,
  type PricingModeEnum,
  type SideEnum,
  type TimeInForceEnum,
} from './types';

export function isOrder(entity: any): entity is Order {
  return entity instanceof Order;
}

export interface OrderWarning {
  severity: WarningSeverity;
  topLevelWarnings: OrderWarningItem[];
  warningItems: OrderMarketWarningItem[];
}

export interface OrderWarningItem {
  severity: WarningSeverity;
  label: string;
  value?: NonNullable<ReactNode>;
}

export interface OrderMarketWarningItem {
  severity: WarningSeverity;
  /** Order.Markets[i].Market */
  market: string;
  /** The .Symbol of the market with the warning. Will only be present if there are multiple symbols on the Order.Markets. */
  symbol: string | undefined;
  value: string;
}

export interface IOrderAnnotations {
  Timestamp: string;
  OrderID: string;
  Revision: number;
  ExecutionReportRevision: number;
  ArchivedStatus: ArchivedStatusEnum;
  User: string;
}

// This wraps an IOMSExecutionReport4203
export class Order {
  static readonly rowID = 'OrderID';

  AvgPx?: string;

  ClOrdID!: string;

  Comments?: string;

  /** User expressed outcome for prediction outcome orders. */
  ExpressedOutcome?: ExpressedOutcomeEnum;

  CumFee?: string;

  CumQty!: string;

  CumAmt?: string;

  DecisionStatus!: DecisionStatusEnum;

  ExecID?: string;

  ExecType?: ExecTypeEnum;

  FeeCurrency?: string;

  LeavesQty?: string;

  OrdStatus!: OrdStatusEnum;

  OrdType!: OrdTypeEnum;

  OrderID!: string;

  OrderQty!: string;

  Price?: string;

  Side!: SideEnum;

  /**
   * The currency of the quantity the order was placed with.
   * Note: a currency can be excluded from an order. For example if the order is placed in number of contracts.
   */
  Currency?: string;

  AmountCurrency?: string;

  Strategy?: string;

  SubmitTime!: string;

  Symbol!: string;

  TimeInForce?: TimeInForceEnum;

  TransactTime?: string;

  Text?: string;

  Parameters?: Partial<IStrategyParameters>;

  MultilegParams?: MultilegParameters;

  Timestamp?: string;

  StartTime?: string;

  EndTime?: string;

  User?: string;

  RequestUser?: string;

  ExpectedFillPrice?: string;

  ExpectedFillQty?: string;

  IWouldFilledQty?: string;

  AvgPxAllIn?: string;

  ParentOrderID?: string;

  /**
   * ParentOrderType is a newer property that exists on Orders created as of backend 2.59, and _some_ records going back.
   * Specifically, only CareOrder and WorkingOrder ParentOrderTypes have been backfilled.
   */
  ParentOrderType?: ParentOrderTypeEnum;

  CumTalosFee?: string;

  TalosFeeCurrency?: string;

  Revision!: number;

  RiskStatus?: Record<OrdRiskStatusEnum, boolean>;

  Markets!: Partial<IOMSExecutionReport4203Markets>[];

  AllowedSlippage?: string;

  RFQID?: string;

  ParentRFQID?: string;

  QuoteID?: string;

  AggressorSide?: SideEnum;

  OrigClOrdID?: string;

  OrdRejReason?: OrdRejReasonEnum;

  SessionID?: string;

  Allocation?: AllocationBE;
  SubAccount?: string;
  /** An array of all the sub accounts used. For allocs, will be the alloc members. If no allocs, will just be the sub account in an array. Else undefined. */
  subAccounts: string[] | undefined;

  Group?: string;

  CxlRejReason?: CxlRejReasonEnum;

  LastTradeTime?: string;

  LastRequestTimestamp?: string;

  LastOrderTime?: string;

  RequestSource?: RequestSourceEnum;

  LegSummary?: IOMSExecutionReport4203LegSummary[];
  PricingMode?: PricingModeEnum;
  PricingReference?: string;
  SettleDate?: string;
  ClearingAccount?: string;

  FixingDetails?: FixingDetails;
  /** An array of all the fixing indices referenced on this entity */
  fixingIndices: string[] | undefined;

  /** Only includes LegSummary with MultilegReportingType = Leg */
  get legSummaryLegs(): IOMSExecutionReport4203LegSummary[] | undefined {
    return this.LegSummary?.filter(leg => leg.MultilegReportingType === MultilegReportingTypeEnum.Leg);
  }

  /** Only includes LegSummary with MultilegReportingType = Parent */
  get legSummaryParent(): IOMSExecutionReport4203LegSummary[] | undefined {
    return this.LegSummary?.filter(leg => leg.MultilegReportingType === MultilegReportingTypeEnum.Parent);
  }

  get priceProtection() {
    return this.Parameters?.PriceProtection;
  }

  get reduceOnly() {
    return this.Parameters?.ReduceOnly || ReduceOnlyEnum.Disabled;
  }

  get reduceFirst() {
    return this.Parameters?.ReduceFirst || ReduceFirstEnum.Disabled;
  }

  /**
   * Whether or not there are multiple symbols represented in the Order.Markets array
   * This will be true for the multileg and unified liquidity order cases for example.
   */
  get hasMultiSymbolMarkets(): boolean {
    const symbolsFoundInMarketsArray = uniq(this.Markets.map(m => m.Symbol));
    return symbolsFoundInMarketsArray.length > 1;
  }

  get warning(): OrderWarning | null {
    return getMarketWarnings({ entity: this });
  }

  get unifiedLiquidity(): UnifiedLiquidityEnum {
    return this.Parameters?.UnifiedLiquidity || UnifiedLiquidityEnum.Disabled;
  }

  get warningSeverity(): WarningSeverity {
    return this.warning?.severity || WarningSeverity.NONE;
  }

  get filledPx(): string | undefined {
    if (this.OrdType === OrdTypeEnum.LimitAllIn) {
      return this.AvgPxAllIn;
    }
    return this.AvgPx;
  }

  get selectedMarkets(): string[] {
    return uniq(
      compact(
        this.Markets?.map(market => (market.MarketStatus !== OrderMarketStatusEnum.Disabled ? market.Market : ''))
      )
    );
  }

  get tradedMarkets(): string[] {
    return uniq(compact(this.Markets?.filter(m => toBigWithDefault(m.CumQty, 0).gt(0)).map(m => m.Market)));
  }

  get selectedAndTradedMarkets(): string[] {
    return uniq(this.selectedMarkets.concat(this.tradedMarkets));
  }

  get selectedMarketAccounts(): string[] {
    return uniq(
      compact(
        this.Markets?.map(market =>
          market.MarketStatus !== OrderMarketStatusEnum.Disabled ? market.MarketAccount : ''
        )
      )
    );
  }

  get tradedMarketAccounts(): string[] {
    return uniq(compact(this.Markets?.filter(m => toBigWithDefault(m.CumQty, 0).gt(0)).map(m => m.MarketAccount)));
  }

  get selectedAndTradedMarketAccounts(): string[] {
    return uniq(this.selectedMarketAccounts.concat(this.tradedMarketAccounts));
  }

  get remainQty(): string {
    return Big(this.OrderQty || 0)
      .minus(this.CumQty || 0)
      .toFixed();
  }

  get startTime(): string {
    return this.Parameters?.StartTime ?? this.StartTime ?? '';
  }

  get endTime(): string {
    const parameterEndTime = this.Parameters?.EndTime;
    if (parameterEndTime) {
      return parameterEndTime;
    }
    return this.EndTime ?? '';
  }

  /** This is this.SubmitTime minus one minute to not fall victim to rounding issues in differences between Order.SubmitTime, Trade.TransactTime, etc... */
  submitTimeForRelatedEntitySub: string;

  customerOrderID?: string;

  customerSymbol?: string;

  customerOrder?: string;

  customerAccount?: string;

  customerQuantity?: string;

  customerFilledQuantity?: string;

  customerFilledCumAmt?: string;

  customerAmountCurrency?: string;

  customerPrice?: string;

  customerAvgPx?: string;

  customerStrategy?: string;

  customerParameters?: Partial<IStrategyParameters>;

  customerPricingParams?: CustomerPricingParameters;

  customerSummary?: CustomerDealSummary;

  get allowedSlippageBPS(): string {
    return percentToBps(this.AllowedSlippage);
  }

  get isArchived(): boolean {
    return this.OrderAnnotations?.ArchivedStatus === ArchivedStatusEnum.Archived;
  }

  get isArchivable(): boolean {
    return !this.isArchived && this.isComplete;
  }

  get isUnArchivable(): boolean {
    return this.isArchived;
  }

  get isCancelable(): boolean {
    return !(this.isPendingOrdStatus || this.isComplete);
  }

  get isModifiable(): boolean {
    return (
      !(this.isPendingOrdStatus || this.isComplete) &&
      !this.ParentOrderID &&
      this.RequestSource !== RequestSourceEnum.Hedger
    );
  }

  // Note: this class represents a principal order in the blotter, based on the optional customer fields we decide
  // if we allow initializing customer order modification workflow
  get isCustomerOrderModifiable(): boolean {
    const canModify = !(this.isPendingOrdStatus || this.isComplete);
    const customerPropertiesLoaded =
      !!this.ParentOrderID &&
      !!this.customerAccount &&
      !!this.customerQuantity &&
      !!this.customerPrice &&
      !!this.customerPricingParams &&
      !!this.customerStrategy;

    return canModify && customerPropertiesLoaded;
  }

  get isPausable(): boolean {
    // Not calling isPaused here as "SystemPaused" orders are also pausable
    const PAUSABLE_DECISION_STATUSES = [DecisionStatusEnum.Paused, DecisionStatusEnum.Staged] as const;
    return (
      !(this.isPendingDecisionStatus || includesEnumValue(PAUSABLE_DECISION_STATUSES, this.DecisionStatus)) &&
      !this.isComplete
    );
  }

  get isResumable(): boolean {
    return !this.isPendingDecisionStatus && this.isPaused && !this.isComplete;
  }

  get isComplete(): boolean {
    return isOrderComplete(this.OrdStatus);
  }

  get isPaused(): boolean {
    return isOrderPaused(this.DecisionStatus);
  }

  // Private for now to encourage using the more abstract getters (isCancelable etc.)
  private get isPendingOrdStatus(): boolean {
    return isOrderPending(this.OrdStatus);
  }

  private get isPendingDecisionStatus(): boolean {
    const PENDING_DECISION_STATUSES = [DecisionStatusEnum.PendingPause, DecisionStatusEnum.PendingResume] as const;
    return includesEnumValue(PENDING_DECISION_STATUSES, this.DecisionStatus);
  }

  public get isDDHHedgeOrder() {
    return this.ParentOrderID != null && this.RequestSource === RequestSourceEnum.OrderHedger;
  }

  public get isDDHParentOrder() {
    return this.Parameters?.DynamicDeltaHedgeStrategy != null;
  }

  public get parentIsCustomerOrder() {
    if (this.ParentOrderType != null) {
      // ParentOrderType exists as of backend 2.59, but is not backfilled. So use it if it exists, otherwise fallback.
      return this.ParentOrderType === ParentOrderTypeEnum.CustomerOrder;
    }

    // For older Orders, if there is no ParentOrderType, we can look at at the RequestSource to maybe get some information. Otherwise, false ("don't know, probably not").
    return this.RequestSource === RequestSourceEnum.Customer ? true : false;
  }

  /**
   * Returns true if ParentOrderType is either care order or working order
   * For Orders, Orders that are children of a CareOrder or WorkingOrder will always have ParentOrderType populated, so we can rely on this
   * (different from ParentOrderType=CustomerOrder for example, which isn't backfilled)
   */
  public get parentIsCareOrder() {
    return (
      this.ParentOrderType === ParentOrderTypeEnum.CareOrder ||
      this.ParentOrderType === ParentOrderTypeEnum.WorkingOrder
    );
  }

  /** Returns the ParentOrderID only if the Parent Order is a CustomerOrder */
  public get customerParentOrderID() {
    return this.parentIsCustomerOrder ? this.ParentOrderID : undefined;
  }

  /** Returns the ParentOrderID only if the RequestSource is OrderHedger */
  public get ddhParentOrderID() {
    return this.RequestSource === RequestSourceEnum.OrderHedger ? this.ParentOrderID : undefined;
  }

  /**
   * Order blotter displays Order objects originating from ORDERS subscription, however for Customer Orders
   * we would like the rows to contain customer specific properties which are extracted from CUSTOMER_ORDERS subscription
   */
  public enrichOrderWithCustomerOrder(customerOrder?: CustomerOrder): void {
    this.customerOrderID = customerOrder?.OrderID;
    this.customerSymbol = customerOrder?.Symbol;
    this.customerOrder = customerOrder?.Counterparty;
    this.customerAccount = customerOrder?.MarketAccount;
    this.customerQuantity = customerOrder?.OrderQty;
    this.customerFilledQuantity = customerOrder?.CumQty;
    this.customerFilledCumAmt = customerOrder?.CumAmt;
    this.customerAmountCurrency = customerOrder?.AmountCurrency;
    this.customerPrice = customerOrder?.Price;
    this.customerAvgPx = customerOrder?.AvgPx;
    this.customerSummary = customerOrder?.summary;
    this.customerPricingParams = customerOrder?.PricingParameters;
    this.customerStrategy = customerOrder?.Strategy;
    this.customerParameters = customerOrder?.Parameters;
  }

  // There might be a better way to do this but at least we should be uniform
  public get hasBeenEnrichedWithCustomerOrder(): boolean {
    return this.customerOrderID !== undefined;
  }

  public HedgeOrderStatus: HedgeOrderStatus | undefined;

  public enrichWithHedgeOrderStatus(hedgeOrderStatus: HedgeOrderStatus | IHedgeOrderStatus): void {
    this.HedgeOrderStatus = new HedgeOrderStatus(hedgeOrderStatus);
  }

  public OrderAnnotations: IOrderAnnotations | undefined;

  public enrichWithOrderAnnotation(orderAnnotations: IOrderAnnotations): void {
    this.OrderAnnotations = orderAnnotations;
  }

  constructor(data: Order) {
    Object.assign(this, data);

    if (data.HedgeOrderStatus) {
      this.enrichWithHedgeOrderStatus(data.HedgeOrderStatus);
    }

    if (data.OrderAnnotations) {
      this.enrichWithOrderAnnotation(data.OrderAnnotations);
    }

    this.subAccounts = getEntitySubAccountNames(data);
    this.fixingIndices = getFixingIndicesList(this.FixingDetails);
    this.submitTimeForRelatedEntitySub = getSubmitTimeForRelatedEntitySub(this.SubmitTime);
  }
}

/** Minus one minute to not fall victim to rounding issues in differences between Order.SubmitTime, Trade.TransactTime, etc... */
function getSubmitTimeForRelatedEntitySub(submitTime: string): string {
  try {
    return formattedDateForSubscription(subMinutes(new Date(submitTime), 1));
  } catch (e) {
    return submitTime;
  }
}

interface MultilegParameters {
  LegParams: MultilegParametersLegParams[];
}

/** IMultilegParameters12001LegParams but with boolean instead of BoolEnum to match actual WS message */
interface MultilegParametersLegParams {
  MaxRestingLevels: number;
  CrossingAllowed: boolean;
  Initiating: boolean;
  HaircutRatio: string;
}

export const getMarketWarnings = ({
  entity,

  includeTopLevelText = true,
}: {
  entity: Pick<Order, 'OrdStatus' | 'Markets' | 'DecisionStatus' | 'Text'>;

  includeTopLevelText?: boolean;
}): OrderWarning | null => {
  // Do not show warnings for completed orders
  if (isOrderComplete(entity.OrdStatus)) {
    return null;
  }

  // If there's more than one symbol represented in the Markets array
  const hasMultiSymbolMarkets = uniq(compact(entity.Markets.map(m => m.Symbol))).length > 1;

  // Disabled market means unselected by user, we ignore these
  const selectedMarkets = entity.Markets.filter(m => m.MarketStatus !== OrderMarketStatusEnum.Disabled);

  if (selectedMarkets.length === 0) {
    return null;
  }

  // Out of selected markets, find which that are not tradable. We want to show warnings for these.
  const untradableMarkets = selectedMarkets.filter(market => {
    // Specific case: if the order is paused, and market data is stale, we choose to not surface this warning - its fine
    if (
      entity.DecisionStatus === DecisionStatusEnum.Paused &&
      market.MarketStatus === OrderMarketStatusEnum.MarketDataStale
    ) {
      return false;
    }

    return market.MarketStatus !== OrderMarketStatusEnum.Online;
  });

  const topLevelWarnings: OrderWarningItem[] = [];

  if (includeTopLevelText) {
    // If there is a top level .Text on the order, we'll want to include it in topLevelWarnings.
    if (entity.Text) {
      if (entity.Text.includes('Protection')) {
        topLevelWarnings.push({ label: entity.Text, severity: WarningSeverity.LOW });
      } else {
        topLevelWarnings.push({ label: entity.Text, severity: WarningSeverity.MEDIUM });
      }
    }
  }

  if (untradableMarkets.length === selectedMarkets.length) {
    topLevelWarnings.push({
      severity: WarningSeverity.HIGH,
      label: `No markets are tradable`,
      value: `0 / ${selectedMarkets.length}`,
    });
  } else if (untradableMarkets.length > selectedMarkets.length / 2) {
    topLevelWarnings.push({
      severity: WarningSeverity.MEDIUM,
      label: `Less than 50% of markets are tradable`,
      value: `${untradableMarkets.length} / ${selectedMarkets.length}`,
    });
  } else if (untradableMarkets.length > 0) {
    topLevelWarnings.push({
      severity: WarningSeverity.LOW,
      label: `Not all markets are tradable`,
      value: `${untradableMarkets.length} / ${selectedMarkets.length}`,
    });
  }
  const severity = max(topLevelWarnings.map(w => w.severity)) || WarningSeverity.NONE;

  if (severity === WarningSeverity.NONE) {
    return null;
  }

  // For untradable markets, display the error text.
  const warningItems: OrderMarketWarningItem[] = compact(
    untradableMarkets.map(untradableMarket => {
      if (untradableMarket.Market) {
        return {
          severity: WarningSeverity.LOW,
          market: untradableMarket.Market,
          symbol: hasMultiSymbolMarkets ? untradableMarket.Symbol : undefined,
          value:
            untradableMarket.Text ??
            (untradableMarket.MarketStatus ? prettyName(untradableMarket.MarketStatus) : 'Market is not tradable'),
        };
      }
      return null;
    })
  );

  return { topLevelWarnings, severity, warningItems };
};

/**
 * For STF orders, an Order is in the "PendingFix" state if it is in Filled. Once a fixing is applied, the STF order
 * goes from Filled -> DoneForDay.
 */
export function isOrderPendingFix(order: Pick<Order, 'PricingMode' | 'OrdStatus'>) {
  return order.OrdStatus === 'Filled' && order.PricingMode === 'SpreadToFixing';
}

const NOT_STARTED_DECISION_STATUSES = new Set([DecisionStatusEnum.Staged, DecisionStatusEnum.WaitingForStartTime]);

/**
 * Returns whether an order decision has started execution.
 * Orders in `Staged` or `WaitingForStartTime` are considered not started.
 */
export const isOrderStarted = (ordDecisionStatus: DecisionStatusEnum): boolean =>
  !includesEnumValueForSet(NOT_STARTED_DECISION_STATUSES, ordDecisionStatus);
